import React, { useState, useEffect, useRef, useMemo } from 'react';
import { normalizarTexto } from '../../../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, ChevronDown, Plus, Search, X } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import { iniciales } from '../utils/helpers';
import ConfirmDialog from '../ConfirmDialog';

/**
 * Control de watchers/suscriptores de una tarea.
 *
 * Dos variantes visuales del trigger; mismo dropdown:
 *   - 'header'  → pill compacto "[Eye] Seguir" / "[Eye] Siguiendo · N ▼".
 *                 Click en el lado izquierdo togglea al usuario actual.
 *                 Click en el chevron abre el dropdown para gestionar otros.
 *   - 'sidebar' → fila de avatares apilados + boton "+".
 *
 * Props:
 *   tareaId, watchers (lista [{empleado_id, nombre}]),
 *   proyecto (con miembros para autocomplete),
 *   currentEmpleadoId,
 *   onChanged (callback tras add/remove para refetch),
 *   variant ('header' | 'sidebar'),
 *   color (hex del proyecto, para tinte de avatares)
 */
const WatchersControl = ({
  tareaId,
  watchers = [],
  proyecto,
  currentEmpleadoId,
  onChanged,
  variant = 'header',
  color = '#06b6d4',
}) => {
  const [open, setOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  // Confirmacion solo cuando quitamos a OTRO. Quitarse uno mismo es trivial:
  // un click vuelve a follow, asi que no preguntamos.
  const [pendingUnfollow, setPendingUnfollow] = useState(null); // { empleado_id, nombre }
  const rootRef = useRef(null);

  const isFollowing = useMemo(
    () => watchers.some((w) => w.empleado_id === currentEmpleadoId),
    [watchers, currentEmpleadoId]
  );

  // Click afuera cierra el dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setBusqueda('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const followEmpleado = async (empleadoId) => {
    try {
      await axiosInstance.post(`/api/tareas/${tareaId}/watchers`, {
        empleado_id: empleadoId,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error agregando watcher:', err);
    }
  };

  const unfollowEmpleado = async (empleadoId) => {
    try {
      await axiosInstance.delete(`/api/tareas/${tareaId}/watchers/${empleadoId}`);
      onChanged?.();
    } catch (err) {
      console.error('Error quitando watcher:', err);
    }
  };

  // Helper para la X de la lista. Si soy yo, quitar directo; si es otro, pido
  // confirmacion (no esperan que alguien mas los desuscriba sin aviso).
  const handleUnfollowClick = (w) => {
    if (w.empleado_id === currentEmpleadoId) {
      unfollowEmpleado(w.empleado_id);
    } else {
      setPendingUnfollow(w);
    }
  };

  const confirmUnfollow = async () => {
    if (!pendingUnfollow) return;
    const id = pendingUnfollow.empleado_id;
    setPendingUnfollow(null);
    await unfollowEmpleado(id);
  };

  const toggleSelf = () => {
    if (!currentEmpleadoId) return;
    if (isFollowing) unfollowEmpleado(currentEmpleadoId);
    else followEmpleado(currentEmpleadoId);
  };

  // Miembros del proyecto que NO son watchers todavia, filtrados por busqueda.
  const candidatos = useMemo(() => {
    const watcherIds = new Set(watchers.map((w) => w.empleado_id));
    const q = normalizarTexto(busqueda).trim();
    return (proyecto?.miembros || [])
      .filter((m) => !watcherIds.has(m.empleado_id))
      .filter((m) => !q || normalizarTexto(m.nombre).includes(q))
      .slice(0, 8);
  }, [proyecto?.miembros, watchers, busqueda]);

  // ---- Renderers compartidos para el dropdown ----

  const dropdown = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -4, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.98 }}
          transition={{ duration: 0.12 }}
          className={`absolute ${
            variant === 'header' ? 'right-0' : 'left-0'
          } top-full mt-1.5 w-72 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 z-50 py-1`}
        >
          {/* Lista de watchers actuales */}
          {watchers.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-slate-400">
              Nadie sigue esta tarea aún.
            </div>
          ) : (
            <div className="max-h-44 overflow-y-auto">
              {watchers.map((w) => (
                <div
                  key={w.empleado_id}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 group"
                >
                  <span
                    className="w-6 h-6 rounded-full text-[10px] font-semibold flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${color}1f`, color }}
                  >
                    {iniciales(w.nombre)}
                  </span>
                  <span className="flex-1 text-sm text-slate-800 truncate">
                    {w.nombre}
                    {w.empleado_id === currentEmpleadoId && (
                      <span className="text-[10px] text-slate-400 ml-1">(tú)</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleUnfollowClick(w)}
                    className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100"
                    title={w.empleado_id === currentEmpleadoId ? 'Dejar de seguir' : 'Quitar'}
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-slate-100 my-1" />

          {/* Buscador + candidatos */}
          <div className="px-2 pb-1">
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Agregar miembro..."
                className="w-full pl-7 pr-2 py-1 text-xs bg-slate-50 border border-transparent rounded placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-300 transition-colors"
              />
            </div>
          </div>

          <div className="max-h-44 overflow-y-auto">
            {candidatos.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-slate-400">
                {busqueda
                  ? 'Sin coincidencias.'
                  : 'Todos los miembros ya siguen esta tarea.'}
              </div>
            ) : (
              candidatos.map((m) => (
                <button
                  key={m.empleado_id}
                  type="button"
                  onClick={() => {
                    followEmpleado(m.empleado_id);
                    setBusqueda('');
                  }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 transition-colors"
                >
                  <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center shrink-0">
                    {iniciales(m.nombre)}
                  </span>
                  <span className="flex-1 truncate text-slate-700">{m.nombre}</span>
                  <Plus size={12} className="text-slate-300" />
                </button>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ---- Variante header: pill compacto con dos zonas clickeables ----

  const confirmDialog = (
    <ConfirmDialog
      isOpen={!!pendingUnfollow}
      title="¿Quitar a este seguidor?"
      message={pendingUnfollow ? `${pendingUnfollow.nombre} dejará de recibir notificaciones de esta tarea. Puedes volver a agregarlo cuando quieras.` : ''}
      variant="warning"
      confirmLabel="Quitar"
      onConfirm={confirmUnfollow}
      onClose={() => setPendingUnfollow(null)}
    />
  );

  if (variant === 'header') {
    return (
      <div ref={rootRef} className="relative">
        <div
          className={`inline-flex items-center h-7 text-xs rounded-md border transition-colors ${
            isFollowing
              ? 'bg-cyan-50 border-cyan-200 text-cyan-700'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
          }`}
        >
          <button
            type="button"
            onClick={toggleSelf}
            disabled={!currentEmpleadoId}
            title={isFollowing ? 'Dejar de seguir' : 'Seguir tarea'}
            className="flex items-center gap-1.5 px-2 h-full rounded-l-md hover:bg-black/[0.03] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye size={13} strokeWidth={isFollowing ? 2.5 : 2} />
            <span className="font-medium">
              {isFollowing ? 'Siguiendo' : 'Seguir'}
              {watchers.length > 0 && (
                <span className="ml-1 tabular-nums opacity-70">· {watchers.length}</span>
              )}
            </span>
          </button>
          <div className={`w-px h-3.5 ${isFollowing ? 'bg-cyan-200' : 'bg-slate-200'}`} />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            title="Gestionar seguidores"
            className="px-1.5 h-full rounded-r-md hover:bg-black/[0.03] transition-colors"
          >
            <ChevronDown
              size={12}
              className={`transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
        {dropdown}
        {confirmDialog}
      </div>
    );
  }

  // ---- Variante sidebar: avatares apilados + boton "+" ----

  const visibles = watchers.slice(0, 4);
  const extra = watchers.length - visibles.length;

  return (
    <div ref={rootRef} className="relative">
      <div className="flex items-center gap-2">
        {watchers.length === 0 ? (
          <span className="text-sm text-slate-400">Nadie</span>
        ) : (
          <div className="flex items-center -space-x-1.5">
            {visibles.map((w) => (
              <span
                key={w.empleado_id}
                title={w.nombre}
                className="w-6 h-6 rounded-full ring-2 ring-white text-[10px] font-semibold flex items-center justify-center"
                style={{ backgroundColor: `${color}20`, color }}
              >
                {iniciales(w.nombre)}
              </span>
            ))}
            {extra > 0 && (
              <span className="w-6 h-6 rounded-full ring-2 ring-white bg-slate-50 text-slate-500 text-[10px] font-medium flex items-center justify-center">
                +{extra}
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-1 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-slate-700 hover:border-slate-400 transition-colors"
          title="Gestionar seguidores"
        >
          <Plus size={11} strokeWidth={2.5} />
        </button>
      </div>
      {dropdown}
      {confirmDialog}
    </div>
  );
};

export default WatchersControl;
